import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { RefreshCw, Ban, CalendarClock, RotateCcw, PhoneCall, Calculator, Settings2, Warehouse, Plus, Trash2, Package } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtINR, fmtDate } from "@/lib/api";
import { PageHeader, EmptyState, StatusBadge, DemoBadge, Pager } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

// ---------- Rate calculator (admin — shows base + markup + final) ----------
function RateCalculator({ warehouses }) {
  const [form, setForm] = useState({ warehouse_id: "", pickup_pincode: "", delivery_pincode: "", cod: false });
  const [pkgType, setPkgType] = useState("small");
  const [weight, setWeight] = useState("");
  const [dims, setDims] = useState({ length: "", width: "", height: "" });
  const [boxes, setBoxes] = useState([{ weight: "", length: "", width: "", height: "" }]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const calc = async () => {
    setBusy(true);
    setResult(null);
    try {
      const payload = { warehouse_id: form.warehouse_id || undefined, pickup_pincode: form.pickup_pincode || undefined, delivery_pincode: form.delivery_pincode, cod: form.cod, package_type: pkgType };
      if (pkgType === "heavy") {
        payload.boxes = boxes.map((b) => ({ weight: parseFloat(b.weight) || 0, length: parseFloat(b.length) || 0, width: parseFloat(b.width) || 0, height: parseFloat(b.height) || 0 }));
      } else {
        payload.weight = parseFloat(weight) || 0.5;
        payload.length = parseFloat(dims.length) || 0;
        payload.width = parseFloat(dims.width) || 0;
        payload.height = parseFloat(dims.height) || 0;
      }
      const { data } = await api.post("/shipping/calculate-rates", payload);
      setResult(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const setBox = (i, k, v) => setBoxes(boxes.map((b, j) => (j === i ? { ...b, [k]: v } : b)));
  const canCalc = pkgType === "small" ? !!weight : boxes.every((b) => parseFloat(b.weight) > 0);
  const inp = "border-slate-700 bg-slate-900 text-slate-100 text-xs";
  return (
    <div className="grid gap-4 lg:grid-cols-[340px_1fr]" data-testid="rate-calculator">
      <div className="rounded-xl border border-slate-800 bg-[#111827] p-4">
        <h3 className="mb-3 flex items-center gap-2 text-sm font-bold text-slate-100"><Calculator size={14} className="text-orange-400" /> Rate Calculator</h3>
        <div className="space-y-2.5">
          <div>
            <p className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">Pickup warehouse</p>
            <select data-testid="calc-warehouse" value={form.warehouse_id} onChange={(e) => setForm({ ...form, warehouse_id: e.target.value })}
              className="w-full rounded-md border border-slate-700 bg-slate-900 px-2.5 py-2 text-xs text-slate-200">
              <option value="">Default (no warehouse)</option>
              {(warehouses || []).map((w) => <option key={w.id} value={w.id}>{`${w.name} · ${w.pincode}`}</option>)}
            </select>
          </div>
          <div>
            <p className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">Package type</p>
            <div className="grid grid-cols-2 gap-2" data-testid="calc-package-type">
              {[["small", "Small Shipment", "Below 10 KG · single box"], ["heavy", "Heavy Shipment", "10 KG or above · single/multiple boxes"]].map(([v, l, s]) => (
                <button key={v} type="button" data-testid={`pkg-${v}`} onClick={() => setPkgType(v)}
                  className={`rounded-lg border p-2.5 text-left transition-colors ${pkgType === v ? "border-orange-500 bg-orange-500/10" : "border-slate-700 hover:border-slate-600"}`}>
                  <p className={`text-[11px] font-bold ${pkgType === v ? "text-orange-400" : "text-slate-200"}`}>{l}</p>
                  <p className="mt-0.5 text-[9px] text-slate-500">{s}</p>
                </button>
              ))}
            </div>
          </div>
          {!form.warehouse_id && (
            <div>
              <p className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">Pickup pincode</p>
              <Input data-testid="calc-pickup-pincode" value={form.pickup_pincode} onChange={(e) => setForm({ ...form, pickup_pincode: e.target.value })} placeholder="500011" className={inp} />
            </div>
          )}
          <div>
            <p className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">Delivery pincode</p>
            <Input data-testid="calc-delivery-pincode" value={form.delivery_pincode} onChange={(e) => setForm({ ...form, delivery_pincode: e.target.value })} placeholder="560001" className={inp} />
          </div>
          {pkgType === "small" ? (
            <>
              <div>
                <p className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">Actual weight (KG)</p>
                <Input data-testid="calc-weight" type="number" step="0.1" value={weight} onChange={(e) => setWeight(e.target.value)} placeholder="0.5" className={inp} />
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[["length", "L cm"], ["width", "W cm"], ["height", "H cm"]].map(([k, l]) => (
                  <div key={k}>
                    <p className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">{l}</p>
                    <Input data-testid={`calc-${k}`} type="number" value={dims[k]} onChange={(e) => setDims({ ...dims, [k]: e.target.value })} className={inp} />
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="space-y-2" data-testid="calc-boxes">
              {boxes.map((b, i) => (
                <div key={i} className="rounded-lg border border-slate-800 p-2.5" data-testid={`calc-box-${i}`}>
                  <div className="mb-1.5 flex items-center justify-between">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400"><Package size={10} className="mr-1 inline" />Box {i + 1}</p>
                    {boxes.length > 1 && (
                      <button data-testid={`calc-box-del-${i}`} onClick={() => setBoxes(boxes.filter((_, j) => j !== i))} className="text-slate-500 hover:text-rose-400"><Trash2 size={11} /></button>
                    )}
                  </div>
                  <div className="grid grid-cols-4 gap-1.5">
                    <Input data-testid={`calc-box-${i}-weight`} type="number" step="0.1" value={b.weight} onChange={(e) => setBox(i, "weight", e.target.value)} placeholder="KG *" className={inp} />
                    <Input data-testid={`calc-box-${i}-length`} type="number" value={b.length} onChange={(e) => setBox(i, "length", e.target.value)} placeholder="L" className={inp} />
                    <Input data-testid={`calc-box-${i}-width`} type="number" value={b.width} onChange={(e) => setBox(i, "width", e.target.value)} placeholder="W" className={inp} />
                    <Input data-testid={`calc-box-${i}-height`} type="number" value={b.height} onChange={(e) => setBox(i, "height", e.target.value)} placeholder="H" className={inp} />
                  </div>
                </div>
              ))}
              <button type="button" data-testid="calc-box-add" onClick={() => setBoxes([...boxes, { weight: "", length: "", width: "", height: "" }])}
                className="flex items-center gap-1 text-[11px] font-semibold text-orange-400 hover:text-orange-300"><Plus size={11} /> Add another box</button>
            </div>
          )}
          <label className="flex items-center gap-2 text-xs text-slate-300">
            <input data-testid="calc-cod" type="checkbox" checked={form.cod} onChange={(e) => setForm({ ...form, cod: e.target.checked })} /> Cash on Delivery
          </label>
          <Button data-testid="calc-submit" onClick={calc} disabled={busy || !canCalc}
            className="w-full bg-orange-500 font-semibold text-white hover:bg-orange-600">{busy ? "Calculating…" : "Get rates"}</Button>
        </div>
      </div>
      <div>
        {result && (
          <div className="mb-3 flex flex-wrap items-center gap-3 rounded-xl border border-slate-800 bg-[#111827] px-4 py-2.5 text-[11px] text-slate-400" data-testid="calc-weights">
            <span>Actual: <b className="text-slate-200">{result.weights.actual} KG</b></span>
            <span>Volumetric: <b className="text-slate-200">{result.weights.volumetric} KG</b></span>
            <span>Chargeable: <b className="text-orange-400">{result.weights.chargeable} KG</b></span>
            {result.weights.box_count > 1 && <span>Boxes: <b className="text-slate-200">{result.weights.box_count}</b></span>}
            <span className="rounded-full bg-slate-800 px-2 py-0.5 font-semibold capitalize text-slate-300">{result.package_type} shipment</span>
            <span className={`rounded-full px-2 py-0.5 font-bold ${result.mode === "demo" ? "bg-amber-500/15 text-amber-400" : "bg-emerald-500/15 text-emerald-400"}`}>{result.mode.toUpperCase()}</span>
          </div>
        )}
        {result?.weights?.boxes && (
          <div className="mb-3 rounded-xl border border-slate-800 bg-[#111827] px-4 py-2.5" data-testid="calc-box-breakdown">
            <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">Per-box chargeable</p>
            <div className="flex flex-wrap gap-2">
              {result.weights.boxes.map((b) => (
                <span key={b.box} className="rounded-full bg-slate-800 px-2.5 py-1 font-mono text-[10px] text-slate-300" data-testid={`box-chargeable-${b.box}`}>
                  Box {b.box}: {b.chargeable} KG <span className="text-slate-500">(act {b.actual} / vol {b.volumetric})</span>
                </span>
              ))}
            </div>
          </div>
        )}
        {!result ? <EmptyState title="Enter shipment details" sub="Rates are filtered by weight capability — a 25 KG shipment only shows heavy-capacity services." /> : !result.services.length ? (
          <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-6 text-center text-xs text-rose-300" data-testid="calc-none">
            No courier service can handle {result.weights.chargeable} KG on this route.
          </div>
        ) : (
          <div className="space-y-2" data-testid="calc-results">
            {result.services.map((s) => (
              <div key={s.service} data-testid={`calc-service-${s.service}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-800 bg-[#111827] px-4 py-3">
                <div>
                  <p className="text-xs font-bold text-slate-100">{s.name}</p>
                  <p className="mt-0.5 text-[10px] text-slate-500">Chargeable {s.chargeable_weight} KG · EDD {s.eta_days} days{s.cod_fee ? ` · COD fee ₹${s.cod_fee}` : ""}</p>
                  <p className="mt-0.5 font-mono text-[10px] text-slate-500">base ₹{s.base_courier_price} + {s.markup_percentage}% (₹{s.markup_percent_amount}) + ₹{s.fixed_markup} = ₹{s.final_customer_price}</p>
                </div>
                <p className="text-lg font-extrabold text-orange-400" data-testid={`calc-final-${s.service}`}>{fmtINR(s.final_with_cod)}</p>
              </div>
            ))}
            {result.filtered_out.length > 0 && (
              <div className="rounded-xl border border-slate-800 bg-[#111827] px-4 py-3" data-testid="calc-filtered">
                <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">Not applicable for this weight</p>
                {result.filtered_out.map((f, i) => (
                  <p key={i} className="text-[10px] text-slate-500"><span className="text-slate-400">{f.courier}</span> — {f.reason}</p>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Pricing config + warehouses ----------
function PricingWarehouses({ config, warehouses, onChanged }) {
  const [form, setForm] = useState(null);
  const [wh, setWh] = useState({ name: "", pincode: "", phone: "", city: "", state: "" });
  const cfg = form || config;
  const saveCfg = useMutation({
    mutationFn: async () => (await api.put("/admin/shipping/config", {
      markup_type: cfg.markup_type, markup_percent: Number(cfg.markup_percent), markup_fixed: Number(cfg.markup_fixed),
      volumetric_divisor: Number(cfg.volumetric_divisor), cod_fee: Number(cfg.cod_fee),
    })).data,
    onSuccess: () => { toast.success("Pricing saved — applies immediately to calculator and booking"); setForm(null); onChanged(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const addWh = useMutation({
    mutationFn: async () => (await api.post("/admin/shipping/warehouses", wh)).data,
    onSuccess: () => { toast.success("Warehouse added — global pricing applies automatically"); setWh({ name: "", pincode: "", phone: "", city: "", state: "" }); onChanged(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const delWh = useMutation({
    mutationFn: async (id) => api.delete(`/admin/shipping/warehouses/${id}`),
    onSuccess: () => { toast.success("Warehouse removed"); onChanged(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const inp = "border-slate-700 bg-slate-900 text-slate-100 text-xs";
  if (!cfg) return null;
  return (
    <div className="grid gap-4 lg:grid-cols-2" data-testid="pricing-warehouses">
      <div className="rounded-xl border border-slate-800 bg-[#111827] p-4">
        <h3 className="mb-3 flex items-center gap-2 text-sm font-bold text-slate-100"><Settings2 size={14} className="text-orange-400" /> Customer pricing markup</h3>
        <p className="mb-3 text-[11px] text-slate-500">One global formula for every warehouse: <span className="font-mono text-slate-300">final = base + base × % + fixed</span></p>
        <div className="space-y-2.5">
          <div>
            <p className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">Markup type</p>
            <select data-testid="cfg-markup-type" value={cfg.markup_type} onChange={(e) => setForm({ ...cfg, markup_type: e.target.value })}
              className="w-full rounded-md border border-slate-700 bg-slate-900 px-2.5 py-2 text-xs text-slate-200">
              <option value="percent_fixed">Percentage + Fixed</option>
              <option value="percent">Percentage only</option>
              <option value="fixed">Fixed only</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <p className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">Markup %</p>
              <Input data-testid="cfg-percent" type="number" step="0.5" value={cfg.markup_percent} onChange={(e) => setForm({ ...cfg, markup_percent: e.target.value })} className={inp} />
            </div>
            <div>
              <p className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">Fixed ₹</p>
              <Input data-testid="cfg-fixed" type="number" step="1" value={cfg.markup_fixed} onChange={(e) => setForm({ ...cfg, markup_fixed: e.target.value })} className={inp} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <p className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">Volumetric divisor</p>
              <Input data-testid="cfg-divisor" type="number" value={cfg.volumetric_divisor} onChange={(e) => setForm({ ...cfg, volumetric_divisor: e.target.value })} className={inp} />
            </div>
            <div>
              <p className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">COD fee ₹</p>
              <Input data-testid="cfg-cod-fee" type="number" value={cfg.cod_fee} onChange={(e) => setForm({ ...cfg, cod_fee: e.target.value })} className={inp} />
            </div>
          </div>
          <p className="rounded-lg bg-slate-900 px-3 py-2 font-mono text-[10px] text-slate-400" data-testid="cfg-example">
            Example: base ₹100 → ₹{Math.round(100 + 100 * (cfg.markup_percent || 0) / 100 + Number(cfg.markup_fixed || 0))} · base ₹250 → ₹{Math.round(250 + 250 * (cfg.markup_percent || 0) / 100 + Number(cfg.markup_fixed || 0))}
          </p>
          <Button data-testid="cfg-save" onClick={() => saveCfg.mutate()} disabled={saveCfg.isPending}
            className="w-full bg-orange-500 font-semibold text-white hover:bg-orange-600">{saveCfg.isPending ? "Saving…" : "Save pricing"}</Button>
        </div>
      </div>
      <div className="rounded-xl border border-slate-800 bg-[#111827] p-4">
        <h3 className="mb-3 flex items-center gap-2 text-sm font-bold text-slate-100"><Warehouse size={14} className="text-orange-400" /> Warehouses</h3>
        <div className="mb-3 space-y-2">
          {(warehouses || []).map((w) => (
            <div key={w.id} data-testid={`warehouse-${w.id}`} className="flex items-center justify-between rounded-lg border border-slate-800 px-3 py-2">
              <div className="text-xs">
                <p className="font-semibold text-slate-200">{w.name}</p>
                <p className="font-mono text-[10px] text-slate-500">{w.pincode}{w.city ? ` · ${w.city}` : ""}{w.state ? `, ${w.state}` : ""}{w.phone ? ` · ${w.phone}` : ""} · global pricing</p>
              </div>
              <button data-testid={`warehouse-del-${w.id}`} onClick={() => delWh.mutate(w.id)} className="text-slate-500 hover:text-rose-400"><Trash2 size={13} /></button>
            </div>
          ))}
          {!warehouses?.length && <p className="text-[11px] text-slate-500">No warehouses yet — rates use a default origin. Add warehouses to route pickups.</p>}
        </div>
        <div className="space-y-2 border-t border-slate-800 pt-3">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Add warehouse</p>
          <Input data-testid="wh-name" value={wh.name} onChange={(e) => setWh({ ...wh, name: e.target.value })} placeholder="Warehouse name" className={inp} />
          <div className="grid grid-cols-3 gap-2">
            <Input data-testid="wh-pincode" value={wh.pincode} onChange={(e) => setWh({ ...wh, pincode: e.target.value.replace(/\D/g, "").slice(0, 6) })} placeholder="Pincode *" className={inp} />
            <Input data-testid="wh-phone" value={wh.phone} onChange={(e) => setWh({ ...wh, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })} placeholder="Phone * (10-digit)" className={inp} />
            <Input data-testid="wh-city" value={wh.city} onChange={(e) => setWh({ ...wh, city: e.target.value })} placeholder="City" className={inp} />
          </div>
          <Input data-testid="wh-state" value={wh.state} onChange={(e) => setWh({ ...wh, state: e.target.value })} placeholder="State" className={inp} />
          <Button data-testid="wh-add" size="sm" onClick={() => addWh.mutate()} disabled={addWh.isPending || !wh.name || wh.pincode.length !== 6 || wh.phone.length !== 10}
            className="bg-orange-500 text-xs font-semibold text-white hover:bg-orange-600"><Plus size={12} /> Add warehouse</Button>
          <p className="text-[10px] text-slate-500">Phone is required — couriers call this number for pickup coordination.</p>
        </div>
      </div>
    </div>
  );
}

export default function Shipping() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);

  const { data } = useQuery({
    queryKey: ["shipments", page],
    queryFn: async () => (await api.get(`/shipping/shipments?page=${page}&limit=15`)).data,
  });
  const { data: ndr, refetch: refetchNdr } = useQuery({ queryKey: ["ndr"], queryFn: async () => (await api.get("/shipping/ndr")).data });
  const { data: rto, refetch: refetchRto } = useQuery({ queryKey: ["rto"], queryFn: async () => (await api.get("/shipping/rto")).data });
  const { data: shipCfg, refetch: refetchCfg } = useQuery({ queryKey: ["ship-cfg"], queryFn: async () => (await api.get("/admin/shipping/config")).data });
  const { data: whData, refetch: refetchWh } = useQuery({ queryKey: ["ship-warehouses"], queryFn: async () => (await api.get("/admin/shipping/warehouses")).data });
  const refreshShipping = () => { refetchCfg(); refetchWh(); };

  const trackRefresh = useMutation({
    mutationFn: async (awb) => (await api.get(`/shipping/track/${awb}`)).data,
    onSuccess: (r) => { toast.success(`Status: ${r.current_status.replace(/_/g, " ")}`); qc.invalidateQueries({ queryKey: ["shipments"] }); },
  });
  const cancel = useMutation({
    mutationFn: async (awb) => api.post(`/shipping/${awb}/cancel`),
    onSuccess: () => { toast.success("Shipment cancelled"); qc.invalidateQueries({ queryKey: ["shipments"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const ndrAction = useMutation({
    mutationFn: async ({ awb, action }) => api.post(`/shipping/${awb}/ndr/action`, { action }),
    onSuccess: () => { toast.success("NDR updated"); refetchNdr(); refetchRto(); qc.invalidateQueries({ queryKey: ["shipments"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const rtoReceive = useMutation({
    mutationFn: async ({ awb, condition }) => (await api.post(`/shipping/${awb}/rto/receive`, { condition })).data,
    onSuccess: (r) => { toast.success(r.restocked ? "RTO received & restocked" : "RTO received — marked damaged, NOT restocked"); refetchRto(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const markNdr = useMutation({
    mutationFn: async (awb) => api.post(`/shipping/${awb}/ndr`, { reason: "Customer unavailable" }),
    onSuccess: () => { toast.success("NDR raised"); refetchNdr(); qc.invalidateQueries({ queryKey: ["shipments"] }); },
  });

  const { data: health } = useQuery({
    queryKey: ["int-health-shipping"],
    queryFn: async () => (await api.get("/admin/integrations/health")).data,
    staleTime: 60000,
  });

  return (
    <div data-testid="shipping-page">
      <PageHeader title="Shipping Console" sub="IndiaFast shipments, AWB tracking, NDR and RTO management." testid="shipping-title">
        {health?.indiafast?.mode === "live" ? (
          <span data-testid="shipping-live-badge" className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-600">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />ShipAny LIVE
          </span>
        ) : (
          <DemoBadge label="INDIAFAST DEMO MODE" />
        )}
      </PageHeader>

      <Tabs defaultValue="shipments">
        <TabsList className="border border-slate-800 bg-[#111827]">
          <TabsTrigger value="shipments" data-testid="tab-shipments">Shipments</TabsTrigger>
          <TabsTrigger value="calculator" data-testid="tab-calculator">Rate Calculator</TabsTrigger>
          <TabsTrigger value="pricing" data-testid="tab-pricing">Pricing & Warehouses</TabsTrigger>
          <TabsTrigger value="ndr" data-testid="tab-ndr">NDR ({ndr?.items?.length || 0})</TabsTrigger>
          <TabsTrigger value="rto" data-testid="tab-rto">RTO ({rto?.items?.length || 0})</TabsTrigger>
        </TabsList>

        <TabsContent value="calculator" className="mt-4">
          <RateCalculator warehouses={whData?.items || []} />
        </TabsContent>

        <TabsContent value="pricing" className="mt-4">
          <PricingWarehouses config={shipCfg} warehouses={whData?.items || []} onChanged={refreshShipping} />
        </TabsContent>

        <TabsContent value="shipments" className="mt-4">
          {!data?.items?.length ? <EmptyState title="No shipments yet" sub="Ship an order to generate an AWB." /> : (
            <div className="overflow-x-auto rounded-xl border border-slate-800 bg-[#111827]">
              <table className="w-full text-left text-xs" data-testid="shipments-table">
                <thead>
                  <tr className="border-b border-slate-800 text-[10px] uppercase tracking-wider text-slate-500">
                    <th className="px-4 py-3">AWB</th>
                    <th className="px-4 py-3">Order</th>
                    <th className="px-4 py-3">Customer</th>
                    <th className="px-4 py-3">Service</th>
                    <th className="px-4 py-3">Cost</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((s) => (
                    <tr key={s.id} data-testid={`shipment-row-${s.awb}`} className="border-b border-slate-800/50 hover:bg-slate-900/40">
                      <td className="px-4 py-3 font-mono text-slate-200">{s.awb}</td>
                      <td className="px-4 py-3 font-mono text-slate-400">{s.order_no}</td>
                      <td className="px-4 py-3 text-slate-400">{s.customer?.name}</td>
                      <td className="px-4 py-3 capitalize text-slate-400">{s.service}</td>
                      <td className="px-4 py-3 text-slate-300">{fmtINR(s.cost)}</td>
                      <td className="px-4 py-3"><StatusBadge status={s.status} /></td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1.5">
                          <Button data-testid={`track-${s.awb}`} size="sm" variant="outline" onClick={() => trackRefresh.mutate(s.awb)}
                            className="border-slate-700 text-slate-300 hover:bg-slate-800"><RefreshCw size={11} /></Button>
                          {["in_transit", "out_for_delivery"].includes(s.status) && (
                            <Button data-testid={`ndr-${s.awb}`} size="sm" variant="outline" onClick={() => markNdr.mutate(s.awb)}
                              className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10"><PhoneCall size={11} /></Button>
                          )}
                          {!["delivered", "cancelled", "rto_delivered"].includes(s.status) && (
                            <Button data-testid={`cancel-${s.awb}`} size="sm" variant="outline" onClick={() => cancel.mutate(s.awb)}
                              className="border-rose-500/40 text-rose-400 hover:bg-rose-500/10"><Ban size={11} /></Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {data && <Pager page={data.page} pages={data.pages} onPage={setPage} />}
        </TabsContent>

        <TabsContent value="ndr" className="mt-4">
          {!ndr?.items?.length ? <EmptyState title="No open NDR cases" /> : (
            <div className="space-y-2">
              {ndr.items.map((s) => (
                <div key={s.id} data-testid={`ndr-case-${s.awb}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-800 bg-[#111827] px-4 py-3">
                  <div className="text-xs">
                    <p className="font-mono text-slate-200">{s.awb} · {s.order_no}</p>
                    <p className="mt-0.5 text-slate-400">{s.customer?.name} · {s.customer?.mobile}</p>
                    <p className="mt-0.5 text-amber-400">Reason: {s.ndr?.reason} · Attempts: {s.ndr?.attempts || 1}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button data-testid={`ndr-reattempt-${s.awb}`} size="sm" onClick={() => ndrAction.mutate({ awb: s.awb, action: "reattempt" })}
                      className="bg-orange-500 text-white hover:bg-orange-600"><RefreshCw size={12} /> Reattempt</Button>
                    <Button data-testid={`ndr-reschedule-${s.awb}`} size="sm" variant="outline" onClick={() => ndrAction.mutate({ awb: s.awb, action: "reschedule" })}
                      className="border-slate-700 text-slate-300 hover:bg-slate-800"><CalendarClock size={12} /> Reschedule</Button>
                    <Button data-testid={`ndr-rto-${s.awb}`} size="sm" variant="outline" onClick={() => ndrAction.mutate({ awb: s.awb, action: "rto" })}
                      className="border-rose-500/40 text-rose-400 hover:bg-rose-500/10"><RotateCcw size={12} /> RTO</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="rto" className="mt-4">
          {!rto?.items?.length ? <EmptyState title="No RTO cases" /> : (
            <div className="space-y-2">
              {rto.items.map((s) => (
                <div key={s.id} data-testid={`rto-case-${s.awb}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-800 bg-[#111827] px-4 py-3">
                  <div className="text-xs">
                    <p className="font-mono text-slate-200">{s.awb} · {s.order_no}</p>
                    <p className="mt-0.5 text-slate-400">RTO status: <span className="capitalize text-slate-200">{s.rto?.status?.replace(/_/g, " ")}</span>
                      {s.rto?.condition && <> · Condition: <span className="capitalize">{s.rto.condition}</span></>}</p>
                    <p className="mt-0.5 text-slate-500">Initiated {fmtDate(s.rto?.created_at)}</p>
                  </div>
                  {s.rto?.status !== "received" && (
                    <div className="flex gap-2">
                      <Button data-testid={`rto-receive-good-${s.awb}`} size="sm" onClick={() => rtoReceive.mutate({ awb: s.awb, condition: "good" })}
                        className="bg-emerald-600 text-white hover:bg-emerald-700">Receive · Restock</Button>
                      <Button data-testid={`rto-receive-damaged-${s.awb}`} size="sm" variant="outline" onClick={() => rtoReceive.mutate({ awb: s.awb, condition: "damaged" })}
                        className="border-rose-500/40 text-rose-400 hover:bg-rose-500/10">Receive · Damaged</Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
